import type {
  ComplianceConfig,
  ComplianceStatus,
  SubmissionDetail,
} from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { INVOICES_KEY } from '@/features/sales/api';
import { throwApiError } from '@/lib/server-messages';

/** The FIN-08 data layer, and the SAL-04 strip's. */

export const EINVOICE_KEY = ['einvoicing'] as const;

export function useComplianceDashboard() {
  return useQuery({
    queryKey: [...EINVOICE_KEY, 'dashboard'],
    queryFn: async () => {
      const response = await api.einvoicing.dashboard();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/** FIN-08's queue, one page at a time (NFR-42); `status` runs in SQL. */
export function useSubmissions(
  status?: ComplianceStatus | undefined,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...EINVOICE_KEY, 'list', status ?? 'all', page, pageSize],
    queryFn: async () => {
      const response = await api.einvoicing.list({
        query: { ...(status ? { status } : {}), page, pageSize },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useSubmission(submissionId: string | undefined) {
  return useQuery({
    queryKey: [...EINVOICE_KEY, 'detail', submissionId],
    enabled: Boolean(submissionId),
    queryFn: async (): Promise<SubmissionDetail> => {
      const response = await api.einvoicing.get({ params: { submissionId: submissionId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * BR-SAL-04-05 — the strip's status is READ from the E-Invoicing module and
 * never copied onto the invoice, "so the two can never disagree". A posted
 * invoice with no record yet answers 404, which the strip draws as Pending
 * rather than as an error (SAL-04 §5a field 2's own fallback).
 */
export function useInvoiceCompliance(invoiceId: string | undefined) {
  return useQuery({
    queryKey: [...EINVOICE_KEY, 'invoice', invoiceId],
    enabled: Boolean(invoiceId),
    retry: false,
    queryFn: async (): Promise<SubmissionDetail | null> => {
      const response = await api.einvoicing.getByInvoice({
        params: { invoiceId: invoiceId! },
      });
      if (response.status === 404) return null;
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useComplianceConfig() {
  return useQuery({
    queryKey: [...EINVOICE_KEY, 'config'],
    queryFn: async (): Promise<ComplianceConfig> => {
      const response = await api.einvoicing.config();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

function useComplianceMutation<TInput, TOut>(run: (input: TInput) => Promise<TOut>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: EINVOICE_KEY });
      // The invoice list's own reads do not change, but the strip on an open
      // invoice does — and it lives under the sales key.
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
    },
  });
}

export function useSubmitCompliance() {
  return useComplianceMutation(async (submissionId: string) => {
    const response = await api.einvoicing.submit({ params: { submissionId }, body: {} });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useResubmitCompliance() {
  return useComplianceMutation(async (submissionId: string) => {
    const response = await api.einvoicing.resubmit({ params: { submissionId }, body: {} });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

/**
 * The authority's asynchronous answer (BR-FIN-08-14). In the shipped build
 * the mock authority is asked for its decision from this screen, which is
 * what lets a clearance invoice actually reach Accepted; a real jurisdiction
 * replaces the caller, not the handler — TODO(callback-transport).
 */
export function useAuthorityAnswer() {
  return useComplianceMutation(async (submissionId: string) => {
    const response = await api.einvoicing.authorityAnswer({
      params: { submissionId },
      body: { outcome: 'accepted' },
    });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

/** BR-SAL-04-01's gate, from the strip. */
export function useSendToCustomer() {
  return useComplianceMutation(async (invoiceId: string) => {
    const response = await api.einvoicing.sendToCustomer({ params: { invoiceId }, body: {} });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}
